import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Instagram } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { CONFIG, BRAND } from "@/config";
import AnnouncementBar from "./AnnouncementBar";
import yazaLogo from "@/assets/yaza-logo.png";

const links = [
  { to: "/", label: "Home" },
  { to: "/drops", label: "Drops" },
  { to: "/strains", label: "Strains" },
  { to: "/tap-in", label: "Tap In" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => setOpen(false), [loc.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="sticky top-0 z-50">
      <AnnouncementBar />
      <header
        className={`border-b border-yaza-border transition-colors duration-300 ${
          scrolled ? "bg-black/85 backdrop-blur-md" : "bg-black"
        }`}
      >
        <nav className="mx-auto flex max-w-[1600px] items-center justify-between px-5 py-4 md:px-10">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img src={yazaLogo} alt="YAZA" className="h-9 w-auto md:h-11" />
            <span className="font-display text-xl leading-none tracking-wide text-yaza-accent md:text-2xl">EXOTICS</span>
          </Link>

          <ul className="hidden items-center gap-10 md:flex">
            {links.map((l) => (
              <li key={l.to}>
                <NavLink
                  to={l.to}
                  data-testid={`nav-${l.label.toLowerCase().replace(" ", "-")}`}
                  className={({ isActive }) =>
                    `text-xs font-semibold uppercase tracking-[0.2em] transition-colors duration-200 hover:text-yaza-accent ${
                      isActive ? "text-yaza-accent" : "text-white"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-4">
            <a
              href={CONFIG.instagramUrl}
              target="_blank"
              rel="noreferrer"
              className="hidden text-white transition-colors hover:text-yaza-accent md:block"
              data-testid="header-ig-link"
              aria-label="Instagram"
            >
              <Instagram size={20} />
            </a>
            <button
              className="md:hidden text-white"
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {open ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden border-b border-yaza-border bg-black md:hidden"
            data-testid="mobile-menu"
          >
            <ul className="flex flex-col px-5 py-4">
              {links.map((l) => (
                <li key={l.to}>
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(" ", "-")}`}
                    className="block py-3 font-display text-3xl uppercase tracking-wide"
                  >
                    {l.label}
                  </NavLink>
                </li>
              ))}
              <a href={CONFIG.instagramUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 py-3 text-yaza-accent">
                <Instagram size={20} /> Instagram
              </a>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
